import { useCallback, useEffect, useRef, useState } from 'react';
import type { OnboardingConfigDTO, OnboardingStepDTO } from 'front-lib';
import {
    hasSeenModule,
    markModuleSeen,
    resetModule,
} from 'front-lib';

export type UseOnboardingReturn = {
    isActive: boolean;
    currentStep: OnboardingStepDTO | null;
    currentIndex: number;
    totalSteps: number;
    next: () => void;
    previous: () => void;
    skip: () => void;
    replay: () => void;
};

export function useOnboarding(config: OnboardingConfigDTO): UseOnboardingReturn {
    const [isActive, setIsActive] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const mountedRef = useRef(true);
    const checkedRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Auto-start on first mount if not seen
    useEffect(() => {
        if (checkedRef.current) return;
        checkedRef.current = true;

        (async () => {
            const seen = await hasSeenModule(config.module);
            if (!seen && mountedRef.current && config.steps.length > 0) {
                // Small delay to let DOM elements render
                setTimeout(() => {
                    if (mountedRef.current) {
                        setIsActive(true);
                        setCurrentIndex(0);
                    }
                }, 800);
            }
        })();
    }, [config.module, config.steps.length]);

    const next = useCallback(() => {
        if (currentIndex < config.steps.length - 1) {
            setCurrentIndex((i) => i + 1);
        } else {
            setIsActive(false);
            markModuleSeen(config.module);
        }
    }, [currentIndex, config.steps.length, config.module]);

    const previous = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex((i) => i - 1);
        }
    }, [currentIndex]);

    const skip = useCallback(() => {
        setIsActive(false);
        markModuleSeen(config.module);
    }, [config.module]);

    const replay = useCallback(async () => {
        await resetModule(config.module);
        setCurrentIndex(0);
        setIsActive(true);
    }, [config.module]);

    return {
        isActive,
        currentStep: isActive ? config.steps[currentIndex] ?? null : null,
        currentIndex,
        totalSteps: config.steps.length,
        next,
        previous,
        skip,
        replay,
    };
}
